import React, { useEffect, useRef, useState } from 'react';

import { post_recalc } from '../api';
import { api_error, fmt_count, is_permission_error } from '../format';
import { blocker_reason, lifecycle_label, recalc_failure, recalc_outcome } from './adapters';
import { recalc_payload } from '../additional_payments/upload/requests';

/*
 * PROD-7828 R10 — "D97": a month with any non-empty needs_recalc / compute_error teacher cannot be
 * reported. Lists those teachers and recalculates them through POST /recalc (CD-33), one teacher at a
 * time, with a running count and a Stop that keeps what finished (R10-C10). on_finished(tally) lets
 * the tab re-read /reports/status, which decides whether the month is still blocked; tally.stopped
 * says Ops stopped the run, with tally.total the teachers it was started for.
 */
const BlockedPanel = ({ blockers, pay_month, month_label, on_finished, on_error }) => {
  const [results, setResults] = useState({});
  // { origin: 'all' | teacher_uuid, done, total } while a run is going.
  const [run, setRun] = useState(null);
  // Stop only takes effect once the teacher in flight returns, so the button says so until then.
  const [stopping, setStopping] = useState(false);
  const stop_ref = useRef(false);
  const user_stop = useRef(false);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const start = (origin, uuids) => {
    if (run || !uuids.length) return;
    stop_ref.current = false;
    user_stop.current = false;
    const tally = { done: 0, failed: 0, skipped: 0, total: uuids.length, stopped: false };
    const finish = () => {
      if (!alive.current) return;
      tally.stopped = user_stop.current && tally.done < tally.total;
      setRun(null);
      setStopping(false);
      on_finished(tally);
    };
    const step = (i) => {
      if (!alive.current) return undefined;
      if (i >= uuids.length || stop_ref.current) {
        finish();
        return undefined;
      }
      setRun({ origin, done: i, total: uuids.length });
      const uuid = uuids[i];
      return post_recalc(recalc_payload(uuid, pay_month))
        .then(recalc_outcome)
        .catch((e) => {
          if (is_permission_error(e)) {
            stop_ref.current = true;
            on_error(e, 'You do not have access to recalculate teacher payments.');
          }
          return recalc_failure(api_error(e).message);
        })
        .then((outcome) => {
          if (!alive.current) return undefined;
          tally.done += 1;
          if (outcome.kind === 'failed') tally.failed += 1;
          if (outcome.kind === 'skipped') tally.skipped += 1;
          setResults((r) => ({ ...r, [uuid]: outcome }));
          return step(i + 1);
        });
    };
    step(0);
  };

  const all_uuids = blockers.map((b) => b.teacher_uuid);
  const running = run !== null;

  return (
    <section className="tp-card tp-rp-blocked" aria-labelledby="tp-rp-blocked-title">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rp-blocked-title">
          Reports for {month_label} are blocked
        </span>
        <span className="tp-actions">
          {running ? (
            <>
              <span className="tp-card-hd-sub" role="status">
                Recalculating {fmt_count(run.done + 1)} of {fmt_count(run.total)}…
              </span>
              <button
                type="button"
                className="tp-btn-ghost"
                aria-disabled={stopping ? 'true' : undefined}
                onClick={() => {
                  if (stopping) return;
                  stop_ref.current = true;
                  user_stop.current = true;
                  setStopping(true);
                }}
              >
                {stopping ? 'Stopping…' : 'Stop'}
              </button>
            </>
          ) : null}
          {/* aria-disabled while its own run goes (CD-114); natively disabled while a row's run goes. */}
          <button
            type="button"
            className="tp-btn"
            aria-disabled={run && run.origin === 'all' ? 'true' : undefined}
            disabled={running && run.origin !== 'all'}
            onClick={() => start('all', all_uuids)}
          >
            Recalculate all ({fmt_count(blockers.length)})
          </button>
        </span>
      </div>
      <div className="tp-rp-blocked-body">
        <p className="tp-rp-note">
          {fmt_count(blockers.length)} {blockers.length === 1 ? 'teacher needs' : 'teachers need'}{' '}
          recalculation before this month can be reported. Files made now would carry out-of-date
          amounts, so every download and the preview wait until they are recalculated.
        </p>
        <ul className="tp-rp-blockers">
          {blockers.map((b) => {
            const outcome = results[b.teacher_uuid];
            const own = run && run.origin === b.teacher_uuid;
            return (
              <li key={b.teacher_uuid} className="tp-rp-blocker">
                <div className="tp-rp-blocker-main">
                  <div className="tp-rp-blocker-name">{b.teacher_name || b.teacher_uuid}</div>
                  <div className="tp-card-field-label">
                    {b.lifecycle ? `${lifecycle_label(b.lifecycle)} · ` : ''}
                    {blocker_reason(b)}
                  </div>
                  {outcome ? (
                    <div className={`tp-rp-outcome tp-rp-outcome-${outcome.kind}`}>
                      {outcome.text}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="tp-btn-out"
                  aria-label={`Recalculate ${b.teacher_name || b.teacher_uuid} now`}
                  aria-disabled={own ? 'true' : undefined}
                  disabled={running && !own}
                  onClick={() => start(b.teacher_uuid, [b.teacher_uuid])}
                >
                  Recalculate now
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default BlockedPanel;
